import React, { useState, useEffect } from 'react';
import Editor from '@monaco-editor/react';
import { KnowledgeModule, KnowledgeModuleCreate } from '../../types';
import { createKnowledgeModule, updateKnowledgeModule } from '../../services/marketingApi';
import { Modal, btn, btnTone, input, label } from '../../components/Kit';

interface ModuleEditorProps {
  module: KnowledgeModule | null;
  onClose: () => void;
  onSuccess: () => void;
}

const ModuleEditor: React.FC<ModuleEditorProps> = ({ module, onClose, onSuccess }) => {
  const isEditing = !!module;

  const [moduleId, setModuleId] = useState(module?.module_id || '');
  const [title, setTitle] = useState(module?.title || '');
  const [displayOrder, setDisplayOrder] = useState(module?.display_order || 0);
  const [jsonContent, setJsonContent] = useState('{}');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (module) {
      setJsonContent(JSON.stringify(module.content, null, 2));
    } else {
      setJsonContent(JSON.stringify({
        module_id: '',
        title: '',
        description: '',
        order: 1,
        media: { type: 'text' },
        content: {},
        suggested_followups: [],
      }, null, 2));
    }
  }, [module]);

  const handleFormat = () => {
    try {
      const parsed = JSON.parse(jsonContent);
      setJsonContent(JSON.stringify(parsed, null, 2));
      setError('');
    } catch (e) {
      setError('Invalid JSON - cannot format');
    }
  };

  const handleSave = async () => {
    setError('');

    if (!moduleId.trim()) {
      setError('Module ID is required');
      return;
    }
    if (!title.trim()) {
      setError('Title is required');
      return;
    }

    let parsedContent: Record<string, any>;
    try {
      parsedContent = JSON.parse(jsonContent);
    } catch (e) {
      setError('Invalid JSON content');
      return;
    }

    const payload: KnowledgeModuleCreate = {
      module_id: moduleId.trim(),
      title: title.trim(),
      display_order: displayOrder,
      content: parsedContent,
    };

    setSaving(true);
    try {
      if (isEditing) {
        await updateKnowledgeModule(module!.id, payload);
      } else {
        await createKnowledgeModule(payload);
      }
      onSuccess();
    } catch (e: any) {
      const detail = e.response?.data?.detail || e.message || 'Failed to save';
      setError(detail);
    } finally {
      setSaving(false);
    }
  };

  const stamp = (iso?: string) => (iso ? new Date(iso).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' }) : '');

  return (
    <Modal
      wide
      title={isEditing ? module!.title : 'New module'}
      onClose={onClose}
      footer={
        <>
          {error
            ? <span className="mr-auto text-brick text-[12.5px]">{error}</span>
            : isEditing && <span className="mr-auto text-graphite text-xs">created {stamp(module?.created_at)} · updated {stamp(module?.updated_at)}</span>}
          <button type="button" onClick={handleFormat} className={`${btn} ${btnTone.ghost}`}>Format JSON</button>
          <button type="button" onClick={onClose} className={`${btn} ${btnTone.charcoal}`}>Cancel</button>
          <button type="button" onClick={handleSave} disabled={saving} className={`${btn} ${btnTone.forest}`}>{saving ? 'Saving…' : 'Save'}</button>
        </>
      }
    >
      <div className="grid gap-4 mb-5" style={{ gridTemplateColumns: 'minmax(0,1fr) minmax(0,2fr) 120px' }}>
        <div>
          <label className={label}>Module ID</label>
          <input type="text" value={moduleId} onChange={(e) => setModuleId(e.target.value)} disabled={isEditing}
                 placeholder="e.g. welcome, pricing" className={`${input} w-full font-mono disabled:bg-charcoal/[0.04] disabled:text-graphite`} />
        </div>
        <div>
          <label className={label}>Title</label>
          <input type="text" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Human-readable title" className={`${input} w-full`} />
        </div>
        <div>
          <label className={label}>Order</label>
          <input type="number" value={displayOrder} onChange={(e) => setDisplayOrder(parseInt(e.target.value) || 0)} className={`${input} w-full text-right`} />
        </div>
      </div>

      <label className={label}>Content</label>
      <div className="h-[56vh] rounded-lg border border-charcoal/15 overflow-hidden">
        <Editor
          height="100%"
          language="json"
          theme="vs-light"
          value={jsonContent}
          onChange={(value) => setJsonContent(value || '{}')}
          options={{
            minimap: { enabled: false },
            fontSize: 13,
            lineNumbers: 'on',
            scrollBeyondLastLine: false,
            wordWrap: 'on',
            tabSize: 2,
            formatOnPaste: true,
          }}
        />
      </div>
    </Modal>
  );
};

export default ModuleEditor;
